import React from "react";
import { Pressable, ScrollView, StyleSheet, Text, View } from "react-native";
import { getBottomInset, getTopInset } from "../utils/safeInsets.js";
import { touchUI } from "../utils/touchUI.js";

const TOP = getTopInset();
const BOTTOM = getBottomInset();

/**
 * Full-screen shell for Screen Time subpages (and similar).
 * Puts Back in a safe top bar so it clears status bar / notches on all Android sizes.
 * Back control is larger on Android/iOS than on web.
 * @param {{ dark?: boolean }} props dark — navy chrome for nested screens
 */
export function SubScreenShell({
  title,
  onBack,
  children,
  scroll = true,
  contentContainerStyle,
  dark = false,
}) {
  const palette = dark
    ? {
        bg: "#0F172A",
        border: "#334155",
        accent: "#F59E0B",
        title: "#F8FAFC",
      }
    : {
        bg: "#FDFDFD",
        border: "#E5E7EB",
        accent: "#0F766E",
        title: "#111827",
      };

  const body = scroll ? (
    <ScrollView
      style={styles.scroll}
      contentContainerStyle={[styles.scrollContent, contentContainerStyle]}
      keyboardShouldPersistTaps="handled"
      showsVerticalScrollIndicator={false}
    >
      {children}
    </ScrollView>
  ) : (
    <View style={[styles.bodyFill, contentContainerStyle]}>{children}</View>
  );

  return (
    <View style={[styles.root, { backgroundColor: palette.bg, paddingTop: TOP, paddingBottom: BOTTOM }]}>
      <View
        style={[
          styles.topBar,
          { backgroundColor: palette.bg, borderBottomColor: palette.border },
        ]}
      >
        <Pressable
          style={styles.backHit}
          onPress={onBack}
          hitSlop={touchUI.hitSlop}
          accessibilityRole="button"
          accessibilityLabel="Back"
        >
          <Text style={[styles.backChevron, { color: palette.accent }]}>‹</Text>
          <Text style={[styles.backLabel, { color: palette.accent }]}>Back</Text>
        </Pressable>
        {title ? (
          <Text style={[styles.title, { color: palette.title }]} numberOfLines={1}>
            {title}
          </Text>
        ) : (
          <View style={styles.titlePlaceholder} />
        )}
        <View style={styles.topBarEnd} />
      </View>
      {body}
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: "#FDFDFD",
  },
  topBar: {
    minHeight: touchUI.topBarMin,
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 8,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: "#E5E7EB",
    backgroundColor: "#FDFDFD",
  },
  backHit: {
    minWidth: touchUI.backMinWidth,
    minHeight: touchUI.minHit,
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: touchUI.backPadH,
    paddingVertical: touchUI.backPadV,
    borderRadius: 12,
  },
  backChevron: {
    fontSize: touchUI.backChevron,
    lineHeight: touchUI.backChevron + 4,
    color: "#0F766E",
    marginTop: -2,
    marginRight: 4,
    fontWeight: "300",
  },
  backLabel: {
    fontSize: touchUI.backLabel,
    fontWeight: "800",
    color: "#0F766E",
  },
  title: {
    flex: 1,
    textAlign: "center",
    fontSize: touchUI.title,
    fontWeight: "800",
    color: "#111827",
    paddingHorizontal: 4,
  },
  titlePlaceholder: { flex: 1 },
  topBarEnd: { minWidth: touchUI.backMinWidth },
  scroll: { flex: 1 },
  bodyFill: {
    flex: 1,
    minHeight: 0,
    width: "100%",
  },
  scrollContent: {
    paddingHorizontal: 16,
    paddingTop: 12,
    paddingBottom: 28,
  },
});
